import type { Player } from '@dreamteam-monorepo/ui';
import type { MatchdayStats, FixturesData } from '@/lib/network';
import type { PlayerMatch } from '@/lib/network/players';
import { getPeriodNumber } from '../../helpers/getPeriodNumber';

export interface LockedPlayerFixtureSide {
  name: string;
  flagKey: string;
}

export interface LockedPlayerFixtureDetails {
  periodLabel: string;
  statusLabel: string;
  centerLabel: string;
  home: LockedPlayerFixtureSide;
  away: LockedPlayerFixtureSide;
  isHome: boolean;
}

const EMPTY_TOTAL = '-';

const getNumericValue = (value: number | null | undefined): number => {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const formatPoints = (value: number | null | undefined): string => {
  const numericValue = getNumericValue(value);
  return numericValue > 0 ? `+${numericValue}` : String(numericValue);
};

const getPeriodLabel = (
  periodNumber: number | null | undefined,
  periodKey?: string,
  periodLabelShort = 'Gameweek'
): string => {
  const resolvedPeriodNumber = periodNumber ?? getPeriodNumber(periodKey);
  return resolvedPeriodNumber ? `${periodLabelShort} ${resolvedPeriodNumber}` : periodLabelShort;
};

const getSortedFixtureKeys = (
  fixturesData: FixturesData | undefined,
  teamFixtures: Record<string, unknown>
): string[] => {
  const keys = fixturesData?.matchDays ?? fixturesData?.gameWeeks ?? Object.keys(teamFixtures);
  return [...keys].sort((left, right) => {
    const leftNumber = getPeriodNumber(left);
    const rightNumber = getPeriodNumber(right);
    if (leftNumber !== null && rightNumber !== null) {
      return leftNumber - rightNumber;
    }
    return left.localeCompare(right);
  });
};

const getFixtureKeyForPeriod = (
  teamFixtures: Record<string, unknown>,
  periodNumber: number | null | undefined,
  fixturesData: FixturesData | undefined
): string | undefined => {
  if (periodNumber) {
    const requestedKeys = [`MD${periodNumber}`, `GW${periodNumber}`, String(periodNumber)];
    const matchingKey = requestedKeys.find(key => Array.isArray(teamFixtures[key]));
    if (matchingKey) {
      return matchingKey;
    }
  }
  return getSortedFixtureKeys(fixturesData, teamFixtures).find(key =>
    Array.isArray(teamFixtures[key])
  );
};

const formatKickoff = (kickoffAt: string | undefined): string => {
  if (!kickoffAt) {
    return 'TBC';
  }
  const date = new Date(kickoffAt);
  if (Number.isNaN(date.getTime())) {
    return 'TBC';
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getPlayerTeamName = (
  player: Pick<Player, 'clubName' | 'country' | 'contestantFlagKey'>
): string => {
  return (
    player.clubName?.trim() || player.country?.trim() || player.contestantFlagKey?.trim() || 'TBC'
  );
};

const getPlayerFlagKey = (
  player: Pick<Player, 'contestantFlagKey' | 'country' | 'shirtKey'>
): string => {
  return (
    player.contestantFlagKey?.trim() || player.country?.trim() || player.shirtKey?.trim() || ''
  );
};

export const buildLockedPlayerFixtureDetails = (
  player: Pick<Player, 'clubName' | 'country' | 'contestantFlagKey' | 'shirtKey' | 'squadId'>,
  fixturesData: FixturesData | undefined,
  periodNumber: number | null | undefined,
  options?: {
    periodLabelShort?: string;
  }
): LockedPlayerFixtureDetails => {
  const playerSide = {
    name: getPlayerTeamName(player),
    flagKey: getPlayerFlagKey(player),
  };
  const team = fixturesData?.teams.find(
    candidate => String(candidate.id) === String(player.squadId)
  );

  const fixtureKey = team
    ? getFixtureKeyForPeriod(team.fixtures, periodNumber, fixturesData)
    : undefined;
  const fixture = fixtureKey ? team?.fixtures[fixtureKey]?.[0] : undefined;
  const opponentSide = {
    name: fixture?.opponentName?.trim() || fixture?.opponentShortName?.trim() || 'TBC',
    flagKey: fixture?.opponentFlagKey?.trim() || '',
  };

  return {
    periodLabel: getPeriodLabel(periodNumber, fixtureKey, options?.periodLabelShort),
    statusLabel: formatKickoff(fixture?.kickoffAt),
    centerLabel: 'v',
    home: fixture?.homeAway === 'A' ? opponentSide : playerSide,
    away: fixture?.homeAway === 'A' ? playerSide : opponentSide,
    isHome: fixture?.homeAway === 'H',
  };
};

interface StatRowMapping {
  label: string;
  statsField: keyof MatchdayStats['stats'];
  breakdownField: keyof MatchdayStats['breakdown'];
  isBoolean?: boolean;
}

const statRowMappings: StatRowMapping[] = [
  { label: 'Goals', statsField: 'goals', breakdownField: 'goals' },
  { label: 'Assists', statsField: 'assists', breakdownField: 'assists' },
  {
    label: 'Fantasy assists',
    statsField: 'fantasyAssists',
    breakdownField: 'fantasy_assists',
  },
  { label: 'Shots on target', statsField: 'shotsOnTarget', breakdownField: 'shots_on_target' },
  {
    label: 'Big chances created',
    statsField: 'chancesCreated',
    breakdownField: 'big_chances_created',
  },
  { label: 'Tackles', statsField: 'tackles', breakdownField: 'tackles' },
  {
    label: 'Clean sheets',
    statsField: 'cleanSheet',
    breakdownField: 'clean_sheet',
    isBoolean: true,
  },
  { label: 'Goals conceded', statsField: 'goalsConceded', breakdownField: 'goals_conceded' },
  { label: 'Saves', statsField: 'saves', breakdownField: 'saves' },
  { label: 'Penalty saves', statsField: 'penaltySaves', breakdownField: 'penalty_saves' },
  { label: 'Yellow cards', statsField: 'yellowCards', breakdownField: 'yellow_cards' },
  { label: 'Red cards', statsField: 'redCards', breakdownField: 'red_cards' },
  { label: 'Own goals', statsField: 'ownGoals', breakdownField: 'own_goals' },
  { label: 'Penalty misses', statsField: 'penaltyMisses', breakdownField: 'penalty_misses' },
];

const ppmBreakdownFields: Array<keyof MatchdayStats['breakdown']> = [
  'ppm_successful_dribble',
  'ppm_accurate_cross',
  'ppm_interception',
  'ppm_block',
  'ppm_goal_outside_area',
  'ppm_foul_won',
  'ppm_offside',
  'ppm_foul_conceded',
  'ppm_error_leading_to_goal',
  'ppm_successful_claim',
  'ppm_successful_punch',
  'ppm_keeper_sweep',
  'ppm_pass_completion_90',
  'ppm_pass_completion_80',
  'ppm_pass_completion_70',
];

const ppmLabelMap: Record<string, string> = {
  ppm_successful_dribble: 'Successful dribble',
  ppm_accurate_cross: 'Accurate cross',
  ppm_interception: 'Interception',
  ppm_block: 'Block',
  ppm_goal_outside_area: 'Goal outside area',
  ppm_foul_won: 'Foul won',
  ppm_offside: 'Offside',
  ppm_foul_conceded: 'Foul conceded',
  ppm_error_leading_to_goal: 'Error leading to goal',
  ppm_successful_claim: 'Successful claim',
  ppm_successful_punch: 'Successful punch',
  ppm_keeper_sweep: 'Keeper sweep',
  ppm_pass_completion_90: 'Pass completion 90%',
  ppm_pass_completion_80: 'Pass completion 80%',
  ppm_pass_completion_70: 'Pass completion 70%',
};

const getPpmMarks = (breakdown: MatchdayStats['breakdown']): number => {
  if (typeof breakdown.ppms_earned === 'number' && Number.isFinite(breakdown.ppms_earned)) {
    return breakdown.ppms_earned;
  }

  return ppmBreakdownFields.reduce((total, field) => total + getNumericValue(breakdown[field]), 0);
};

const isEmptyStatsMatch = (stats: MatchdayStats | null | undefined): boolean => {
  if (!stats) return true;
  const allStatsNull = Object.values(stats.stats).every(value => value === null);
  const breakdownEmpty = !stats.breakdown || Object.keys(stats.breakdown).length === 0;
  const pointsZero = (stats.points ?? 0) === 0 && (stats.ppmPoints ?? 0) === 0;
  return allStatsNull && breakdownEmpty && pointsZero;
};

/** Team-context uplift (captaincy, Max Captain) broken down per match. */
export interface LockedPlayerBonusRow {
  label: string;
  points: number;
  matches?: Record<string, string>;
}

export interface LockedPlayerMatchStatRow {
  label: string;
  total: string;
  points: string;
  emphasized?: boolean;
  details?: Array<{ label: string; total: string }>;
}

/**
 * Points the player scored in ONE match.
 *
 * With Goal Bonus active the match already carries its boosted total in
 * `goalBonusNormalPoints`, so the two sources must never be added together.
 */
export const getLockedPlayerMatchPoints = (
  stats: MatchdayStats | null | undefined,
  activeBoosterCode?: string | null
): number => {
  if (!stats) return 0;
  if (activeBoosterCode === 'goal_bonus' && stats.goalBonusNormalPoints != null) {
    return stats.goalBonusNormalPoints;
  }
  return getNumericValue(stats.points) + getNumericValue(stats.ppmPoints);
};

/**
 * The team-context uplift credited to a single match.
 *
 * `bonusRow.matches` holds the per-match base the multiplier doubled, which for
 * every x2 booster is also the uplift that match contributed. '-' marks a match
 * the booster did not touch.
 */
export const getLockedPlayerMatchBonusPoints = (
  bonusRow: LockedPlayerBonusRow | null | undefined,
  matchId: string
): number => {
  const value = bonusRow?.matches?.[matchId];
  if (!value || value === EMPTY_TOTAL) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Stat rows for one match's accordion (ENG-1134).
 *
 * Every number comes from that match's own breakdown, because the accordion
 * asks "what did this fixture score?" - not "what did the period score?". The
 * two questions have different answers in a double gameweek: PPM marks
 * accumulate across all of the period's matches before the bonus tier is
 * picked, and threshold rules (tackles, keeper saves - one point per two) are
 * decided on period totals. The accordions can therefore add up to less than
 * the period total in the modal header, which is the price of reporting each
 * fixture honestly.
 *
 * A row is listed only when it scored: a stat worth 0 points explains nothing.
 */
export const buildLockedPlayerMatchStatRows = (
  match: PlayerMatch,
  activeBoosterCode?: string | null,
  bonusRow?: LockedPlayerBonusRow | null
): LockedPlayerMatchStatRow[] => {
  const stats = match.stats;
  if (!stats || isEmptyStatsMatch(stats)) {
    return [];
  }

  const breakdown = stats.breakdown ?? {};
  const breakdownPoints = (field: keyof MatchdayStats['breakdown']): number =>
    getNumericValue(breakdown[field]);

  const formatTotal = (value: number | boolean | null | undefined): string =>
    typeof value === 'boolean' ? (value ? '1' : '0') : String(getNumericValue(value));

  const rows: LockedPlayerMatchStatRow[] = [];

  const pushMappingRow = (mapping: StatRowMapping) => {
    let statPoints = breakdownPoints(mapping.breakdownField);
    let label = mapping.label;

    // With Goal Bonus active the boosted goal points replace the regular ones.
    if (mapping.breakdownField === 'goals' && activeBoosterCode === 'goal_bonus') {
      const goalsBonusPoints = breakdownPoints('goals_bonus');
      if (goalsBonusPoints !== 0) {
        statPoints = goalsBonusPoints;
        label = 'Goals (Goal Bonus)';
      }
    }

    if (statPoints === 0) {
      return;
    }

    rows.push({
      label,
      total: formatTotal(stats.stats[mapping.statsField]),
      points: formatPoints(statPoints),
    });
  };

  // Design order: the goal leads, minutes played sits behind it, the remaining
  // stats follow and the PPM tier bonus closes the table.
  const goalsMapping = statRowMappings.find(mapping => mapping.breakdownField === 'goals');
  if (goalsMapping) {
    pushMappingRow(goalsMapping);
  }

  if (typeof stats.minutesPlayed === 'number' && stats.minutesPlayed > 0) {
    rows.push({
      label: 'Minutes played',
      total: String(stats.minutesPlayed),
      points: formatPoints(breakdownPoints('appearance')),
    });
  }

  for (const mapping of statRowMappings) {
    if (mapping === goalsMapping) continue;
    pushMappingRow(mapping);
  }

  // The lines that explain the PPM row, each reporting the MARKS that event
  // contributed rather than how often it happened - the two differ whenever a
  // rule awards one mark per several events, and only the marks add up to the
  // tally on the row above. The event counts in `stats` cannot stand in for
  // them either: a breakdown value is `ruleValue x count` under a rule that is
  // position-dependent and, for fouls conceded, negative - so the count is a
  // different quantity and cannot be recovered from the marks. Negative events
  // (fouls conceded, offsides, errors leading to a goal) keep their sign for
  // the same reason: they reduce the tally that picks the bonus tier, so
  // dropping them makes the visible lines add up to more than it shows
  // (ENG-719).
  const ppmDetails: NonNullable<LockedPlayerMatchStatRow['details']> = [];
  for (const field of ppmBreakdownFields) {
    const marks = breakdownPoints(field);
    if (marks === 0) {
      continue;
    }

    ppmDetails.push({ label: ppmLabelMap[field] ?? field, total: String(marks) });
  }

  const ppmMarks = getPpmMarks(breakdown);
  const ppmPoints = breakdownPoints('bonus_points');
  // A tally that nets to zero (e.g. a foul conceded cancelled out by a
  // dribble) is still a tally: keep the row whenever PPM was recorded for the
  // match, rather than only when the sum happens to be non-zero.
  const hasPpmData =
    ppmDetails.length > 0 || breakdown.ppms_earned != null || breakdown.bonus_points != null;
  if (hasPpmData || ppmMarks !== 0 || ppmPoints !== 0) {
    rows.push({
      label: 'PPM Bonus Points',
      total: String(ppmMarks),
      points: formatPoints(ppmPoints),
      emphasized: true,
      details: ppmDetails.length > 0 ? ppmDetails : undefined,
    });
  }

  // Captaincy and Max Captain double the match, so the uplift belongs to the
  // match that earned it rather than to a table-wide footer.
  const bonusPoints = getLockedPlayerMatchBonusPoints(bonusRow, match.matchId);
  if (bonusRow && bonusPoints !== 0) {
    rows.push({
      label: bonusRow.label,
      total: EMPTY_TOTAL,
      points: formatPoints(bonusPoints),
      emphasized: true,
    });
  }

  return rows;
};
